// @ts-nocheck
import React, { useEffect } from 'react';
import { CheckIcon, ArrowRightIcon, CalendarIcon, StarIcon, QuoteIcon, ShieldCheckIcon, Sparkles } from 'lucide-react';
import { ClinicSettings } from '../types';

// --- PROPS INTERFACE ---
interface CaseReportPDFEliteProps {
    reportData: any;
    originalImageUrl: string;
    generatedImageUrl: string;
    coverImageUrl?: string;
    smileRating: any;
    afterSmileRating: any;
    patientPortraitUrl?: string | null;
    treatmentType: 'cosmetic' | 'orthodontic' | 'restorative' | 'pro-automated' | 'fast-pro';
    clinicSettings?: ClinicSettings;
    onRendered: () => void;
}

// --- GLOBAL STYLES & FONTS ---
const GlobalStyles = () => (
    <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Serif+Display:ital@0;1&family=Inter:wght@200;300;400;500;600;700&display=swap');
        @import url('https://fonts.googleapis.com/css2?family=Alex+Brush&display=swap');
        
        .pdf-page-elite {
            font-feature-settings: "ss01", "ss02";
            -webkit-font-smoothing: antialiased;
        }

        .font-signature {
            font-family: 'Alex Brush', cursive;
        }
    `}</style>
);

// --- TREATMENT CONFIGURATION ---
const treatmentConfigs: Record<string, { color: string; label: string }> = {
    cosmetic: { color: '#14B8A6', label: 'Cosmetic Enhancement' },
    orthodontic: { color: '#8B5CF6', label: 'Orthodontic Alignment' },
    restorative: { color: '#F59E0B', label: 'Restorative Treatment' },
    'pro-automated': { color: '#EC4899', label: 'Pro Transformation' },
    'fast-pro': { color: '#06B6D4', label: 'Fast Pro' }
};

// --- ICON COMPONENTS ---
const EliteFuturaSmileIcon: React.FC<{ className?: string }> = ({ className }) => (
    <svg className={className} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M12 3L13.06 11.06L21 12L13.06 12.94L12 21L10.94 12.94L3 12L10.94 11.06L12 3Z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
    </svg>
);

// --- LAYOUT COMPONENTS ---
const Page: React.FC<{ children: React.ReactNode; className?: string; noBg?: boolean }> = ({ children, className = '', noBg = false }) => (
    <div
        className={`pdf-page-elite relative w-[297mm] h-[210mm] text-[#1E293B] flex flex-col font-sans overflow-hidden ${className}`}
        style={{
            fontFamily: "'Inter', sans-serif",
            pageBreakAfter: 'always',
            background: noBg ? 'transparent' : '#FAFAF9'
        }}
    >
        {!noBg && (
            <div className="absolute inset-0 pointer-events-none opacity-[0.4]" style={{
                backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.8' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)' opacity='0.08'/%3E%3C/svg%3E")`,
                mixBlendMode: 'multiply'
            }} />
        )}
        {!noBg && (
            <>
                <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-slate-200/20 rounded-full blur-[100px] -mr-[200px] -mt-[200px] pointer-events-none" />
                <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-slate-300/10 rounded-full blur-[80px] -ml-[200px] -mb-[200px] pointer-events-none" />
            </>
        )}
        {children}
    </div>
);

const PageHeader: React.FC<{ subtitle?: string; accentColor?: string; clinicName?: string; clinicLogo?: string }> = ({ subtitle, accentColor = '#14B8A6', clinicName, clinicLogo }) => (
    <header className="w-full flex-shrink-0 px-[12mm] pt-[10mm] pb-4 z-20 relative">
        <div className="flex justify-between items-center">
            <div className="flex items-center gap-4">
                {clinicLogo ? (
                    <div className="h-10 w-auto max-w-[140px] opacity-90 grayscale hover:grayscale-0 transition-all">
                        <img src={clinicLogo} alt="Clinic" className="h-full w-full object-contain object-left" />
                    </div>
                ) : (
                    <div className="flex items-center gap-2.5">
                        <div className="w-8 h-8 rounded-lg bg-slate-900 flex items-center justify-center text-white shadow-sm">
                            <EliteFuturaSmileIcon className="w-4 h-4" />
                        </div>
                        <span className="font-bold text-slate-900 tracking-tight text-sm uppercase">{clinicName || 'FUTURASMILE'}</span>
                    </div>
                )}
                <div className="w-px h-5 bg-slate-300" />
                <span className="text-[10px] uppercase font-bold text-slate-400 tracking-[0.2em]">Elite Strategy</span>
            </div>
            {subtitle && (
                <div className="flex items-center gap-3">
                    <span className="text-[9px] font-bold uppercase tracking-[0.25em] text-slate-400">{subtitle}</span>
                    <div className="w-1.5 h-1.5 rounded-full ring-2 ring-offset-2 ring-transparent" style={{ backgroundColor: accentColor }} />
                </div>
            )}
        </div>
    </header>
);

const PageFooter: React.FC<{ pageNumber: number; totalPages: number; accentColor?: string; clinicName?: string }> = ({ pageNumber, totalPages, accentColor = '#14B8A6', clinicName }) => (
    <footer className="w-full mt-auto flex-shrink-0 px-[12mm] pb-[10mm] z-20 relative">
        <div className="flex justify-between items-end border-t border-slate-200 pt-4">
            <div className="flex flex-col gap-1">
                <p className="text-[9px] text-slate-400 font-medium uppercase tracking-wider">AI-Driven Clinical Simulation</p>
                <div className="flex items-center gap-2 text-[8px] text-slate-300 font-bold uppercase tracking-widest">
                    <span>{clinicName || 'FuturaSmile'}</span>
                    <span>•</span>
                    <span>Private & Confidential</span>
                </div>
            </div>
            <div className="flex items-center gap-2 bg-white px-4 py-1.5 rounded-full shadow-sm border border-slate-100/50">
                <span className="text-[10px] font-bold text-slate-900">{pageNumber}</span>
                <span className="text-[10px] text-slate-300">/</span>
                <span className="text-[10px] text-slate-400">{totalPages}</span>
            </div>
        </div>
    </footer>
);

const SectionTitle: React.FC<{ overline: string; title: string; accentColor?: string; centered?: boolean; subtitle?: string; dark?: boolean }> = ({ overline, title, accentColor = '#14B8A6', centered = false, subtitle, dark = false }) => (
    <div className={centered ? 'text-center' : ''}>
        <div className={`flex items-center gap-3 ${centered ? 'justify-center' : ''} mb-4`}>
            {!centered && <div className="w-6 h-[1px]" style={{ backgroundColor: accentColor }} />}
            <p className={`text-[10px] font-bold tracking-[0.3em] uppercase ${dark ? 'text-white/60' : 'text-slate-400'}`}>{overline}</p>
            {centered && <div className="w-6 h-[1px]" style={{ backgroundColor: accentColor }} />}
        </div>
        <h2 className={`text-4xl lg:text-5xl font-normal leading-none mb-4 ${dark ? 'text-white' : 'text-slate-900'}`} style={{ fontFamily: "'DM Serif Display', serif" }}>
            {title}
        </h2>
        {subtitle && (
            <p className={`text-sm leading-relaxed max-w-2xl ${centered ? 'mx-auto' : ''} ${dark ? 'text-white/70' : 'text-slate-500 font-light'}`}>{subtitle}</p>
        )}
    </div>
);

// --- VISUALIZATION COMPONENTS ---
const ScoreRing: React.FC<{ score: number; label: string; size?: number; color?: string }> = ({ score, label, size = 120, color = '#14B8A6' }) => {
    const strokeWidth = 5;
    const radius = (size - strokeWidth * 2) / 2;
    const circumference = 2 * Math.PI * radius;
    const offset = circumference - (score / 100) * circumference;

    return (
        <div className="flex flex-col items-center">
            <div className="relative" style={{ width: size, height: size }}>
                <svg className="w-full h-full -rotate-90 relative z-10" viewBox={`0 0 ${size} ${size}`}>
                    <circle cx={size / 2} cy={size / 2} r={radius} fill="none" stroke="#F1F5F9" strokeWidth={strokeWidth} />
                    <circle cx={size / 2} cy={size / 2} r={radius} fill="none" stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" style={{ strokeDasharray: circumference, strokeDashoffset: offset, transition: 'all 1s ease-out' }} />
                </svg>
                <div className="absolute inset-0 flex flex-col items-center justify-center">
                    <span className="text-3xl font-regular text-slate-800" style={{ fontFamily: "'DM Serif Display', serif" }}>{score}</span>
                </div>
            </div>
            <p className="mt-3 text-[10px] uppercase tracking-widest font-bold text-slate-500">{label}</p>
        </div>
    );
};

const ComparisonMetricBar: React.FC<{ label: string; before: number; after: number; color?: string }> = ({ label, before, after, color = '#14B8A6' }) => (
    <div className="w-full mb-5">
        <div className="flex justify-between items-end mb-2">
            <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500">{label}</span>
            <div className="flex items-center gap-2">
                <span className="text-xs text-slate-400">{before}</span>
                <ArrowRightIcon className="w-3 h-3 text-slate-300" />
                <span className="text-sm font-bold" style={{ color }}>{after}</span>
            </div>
        </div>
        <div className="h-1.5 w-full bg-slate-100 rounded-full overflow-hidden relative">
            <div className="absolute h-full bg-slate-300" style={{ width: `${before}%` }} />
            <div className="absolute h-full opacity-80" style={{ width: `${after}%`, backgroundColor: color }} />
        </div>
    </div>
);

const EliteRadarChart: React.FC<{ before: any[]; after: any[]; color: string }> = ({ before, after, color }) => {
    // Guard against empty or invalid data
    if (!before?.length || !after?.length || before.length < 3) return null;

    const size = 300;
    const center = size / 2;
    const radius = center * 0.7;
    const points = (data: any[]) => data.map((d, i) => {
        const r = radius * ((d.score ?? 0) / 100);
        const a = (Math.PI * 2 * i) / data.length - Math.PI / 2;
        return `${center + r * Math.cos(a)},${center + r * Math.sin(a)}`;
    }).join(' ');

    return (
        <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
            {[0.25, 0.5, 0.75, 1].map((s, i) => (
                <circle key={i} cx={center} cy={center} r={radius * s} fill="none" stroke="#E2E8F0" strokeDasharray="4 4" />
            ))}
            <polygon points={points(before)} fill="#94A3B8" fillOpacity="0.1" stroke="#94A3B8" strokeWidth="1" />
            <polygon points={points(after)} fill={color} fillOpacity="0.2" stroke={color} strokeWidth="2" />
            {before.map((d, i) => {
                const a = (Math.PI * 2 * i) / before.length - Math.PI / 2;
                const x = center + radius * 1.2 * Math.cos(a);
                const y = center + radius * 1.2 * Math.sin(a);
                return (
                    <text key={i} x={x} y={y} textAnchor="middle" dominantBaseline="middle" fontSize="9" fill="#64748B" fontWeight="bold" letterSpacing="0.05em">
                        {d.label.toUpperCase()}
                    </text>
                );
            })}
        </svg>
    );
};

// --- MAIN COMPONENT ---
const CaseReportPDFElite: React.FC<CaseReportPDFEliteProps> = ({
    reportData, originalImageUrl, generatedImageUrl, coverImageUrl, smileRating, afterSmileRating, patientPortraitUrl, treatmentType, clinicSettings, onRendered
}) => {
    useEffect(() => { setTimeout(onRendered, 1000); }, [onRendered]);

    const primaryColor = clinicSettings?.primaryColor || treatmentConfigs[treatmentType]?.color || '#14B8A6';
    const hasPortrait = !!patientPortraitUrl;
    const totalPages = hasPortrait ? 10 : 9;

    const scoreBefore = smileRating?.overallSmileScore?.score ?? 0;
    const scoreAfter = afterSmileRating?.overallSmileScore?.score ?? 0;
    const improvement = scoreAfter - scoreBefore;

    const radarBefore = [
        { label: 'Symmetry', score: smileRating?.detailedScores?.symmetryAndAlignment ?? 0 },
        { label: 'Brightness', score: smileRating?.detailedScores?.colorAndBrightness ?? 0 },
        { label: 'Proportions', score: smileRating?.detailedScores?.shapeAndProportions ?? 0 },
        { label: 'Gum Line', score: smileRating?.detailedScores?.gumHealthAndContour ?? 0 },
    ];
    const radarAfter = [
        { label: 'Symmetry', score: afterSmileRating?.detailedScores?.symmetryAndAlignment ?? 0 },
        { label: 'Brightness', score: afterSmileRating?.detailedScores?.colorAndBrightness ?? 0 },
        { label: 'Proportions', score: afterSmileRating?.detailedScores?.shapeAndProportions ?? 0 },
        { label: 'Gum Line', score: afterSmileRating?.detailedScores?.gumHealthAndContour ?? 0 },
    ];

    let pageNum = 0;

    return (
        <div style={{ background: '#FAFAF9', color: '#1E293B' }}>
            <GlobalStyles />

            {/* PAGE 1: COVER */}
            <Page className="p-0" noBg>
                <div className="absolute inset-0 bg-slate-900">
                    <img
                        src={coverImageUrl || generatedImageUrl}
                        className="w-full h-full opacity-90"
                        style={{ objectFit: 'cover', objectPosition: 'center 20%' }}
                        alt="Cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-r from-slate-950 via-slate-950/80 to-transparent" />
                </div>
                <div className="absolute inset-0 flex flex-col justify-between p-[15mm] text-white z-10">
                    <div className="flex items-center gap-6">
                        {clinicSettings?.clinicLogoBase64 ? (
                            <img src={`data:${clinicSettings.clinicLogoMimeType};base64,${clinicSettings.clinicLogoBase64}`} className="h-16 w-auto object-contain brightness-0 invert" />
                        ) : (
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 bg-white text-slate-900 rounded-lg flex items-center justify-center"><EliteFuturaSmileIcon className="w-5 h-5" /></div>
                                <span className="text-lg font-bold tracking-widest">FUTURASMILE</span>
                            </div>
                        )}
                        <div className="h-8 w-px bg-white/20" />
                        <span className="text-xs font-bold tracking-[0.3em] opacity-60 uppercase">Elite Analysis</span>
                    </div>

                    <div className="max-w-2xl">
                        <span className="inline-block px-4 py-1.5 rounded-full border border-white/20 bg-white/5 backdrop-blur-md text-[10px] font-bold tracking-[0.2em] uppercase mb-6">
                            Confidential Patient Report
                        </span>
                        <h1 className="text-7xl lg:text-8xl leading-none mb-8" style={{ fontFamily: "'DM Serif Display', serif" }}>
                            Your New<br /><span style={{ color: primaryColor }}>Smile Awaits.</span>
                        </h1>
                        <p className="text-lg font-light opacity-80 max-w-lg leading-relaxed border-l-2 border-white/20 pl-6">
                            A bespoke aesthetic strategy generated by advanced AI, tailored specifically for {reportData.patientName || 'our valued patient'}.
                        </p>
                    </div>

                    <div className="flex justify-between items-end border-t border-white/10 pt-8">
                        <div>
                            <p className="text-[9px] uppercase tracking-widest opacity-40 font-bold mb-1">Prepared For</p>
                            <p className="text-2xl" style={{ fontFamily: "'DM Serif Display', serif" }}>{reportData.patientName || 'Valued Patient'}</p>
                        </div>
                        <div className="text-right">
                            {clinicSettings?.doctorName && <p className="text-xl font-signature opacity-80 mb-2">{clinicSettings.doctorName}</p>}
                            <p className="text-[10px] uppercase tracking-widest opacity-40 font-bold">Clinical Director</p>
                        </div>
                    </div>
                </div>
            </Page>

            {/* PAGE 2: EXECUTIVE SUMMARY */}
            <Page>
                <PageHeader subtitle="Executive Summary" accentColor={primaryColor} clinicName={clinicSettings?.clinicName} clinicLogo={clinicSettings?.clinicLogoBase64 ? `data:${clinicSettings.clinicLogoMimeType};base64,${clinicSettings.clinicLogoBase64}` : undefined} />
                <main className="flex-1 px-[12mm] py-4 grid grid-cols-12 gap-8">
                    <div className="col-span-4 flex flex-col gap-6">
                        <div className="relative aspect-square rounded-2xl overflow-hidden shadow-lg border-4 border-white">
                            <img src={originalImageUrl} className="w-full h-full" style={{ objectFit: 'cover' }} alt="Original" />
                            <div className="absolute bottom-4 left-4 bg-white/90 backdrop-blur px-3 py-1 rounded-full text-[10px] font-bold tracking-wider">INITIAL STATE</div>
                        </div>
                        <div className="p-6 bg-slate-50 rounded-2xl border border-slate-100 relative overflow-hidden">
                            <div className="absolute top-0 right-0 p-4 opacity-10"><QuoteIcon className="w-12 h-12 text-slate-900" /></div>
                            <p className="text-[9px] font-bold uppercase tracking-widest text-slate-400 mb-3">Clinical Objective</p>
                            <p className="text-sm font-medium text-slate-700 leading-relaxed" style={{ fontFamily: "'DM Serif Display', serif" }}>
                                "{reportData.introduction?.body || 'To enhance the aesthetic balance and biological harmony of the smile.'}"
                            </p>
                        </div>
                    </div>
                    <div className="col-span-8 flex flex-col justify-center">
                        <SectionTitle overline="Overview" title="The Strategy" accentColor={primaryColor} subtitle="A comprehensive breakdown of the aesthetic enhancements planned for your smile transformation." />
                        <div className="grid grid-cols-2 gap-4 mt-8">
                            <div className="p-5 rounded-2xl border border-slate-100 bg-white hover:shadow-md transition-shadow">
                                <div className="flex items-center gap-3 mb-2">
                                    <div className="w-8 h-8 rounded-full flex items-center justify-center text-white" style={{ background: primaryColor }}><Sparkles className="w-4 h-4" /></div>
                                    <p className="text-xs font-bold uppercase tracking-wider text-slate-700">Aesthetic Score</p>
                                </div>
                                <div className="flex items-baseline gap-2">
                                    <span className="text-4xl font-normal" style={{ fontFamily: "'DM Serif Display', serif" }}>+{improvement}</span>
                                    <span className="text-xs text-slate-400 font-medium">Points Gained</span>
                                </div>
                            </div>
                            <div className="p-5 rounded-2xl border border-slate-100 bg-white hover:shadow-md transition-shadow">
                                <div className="flex items-center gap-3 mb-2">
                                    <div className="w-8 h-8 rounded-full flex items-center justify-center text-white" style={{ background: '#0F172A' }}><ShieldCheckIcon className="w-4 h-4" /></div>
                                    <p className="text-xs font-bold uppercase tracking-wider text-slate-700">Treatment Tier</p>
                                </div>
                                <p className="text-lg font-medium text-slate-800">{treatmentConfigs[treatmentType]?.label}</p>
                            </div>
                        </div>
                        <div className="mt-8 space-y-3">
                            <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Key Outcomes</p>
                            {(reportData.afterVision?.keyChanges || []).slice(0, 3).map((c, i) => (
                                <div key={i} className="flex items-center gap-3 p-3 bg-white rounded-xl border border-slate-100">
                                    <CheckIcon className="w-4 h-4 text-emerald-500" />
                                    <span className="text-xs font-medium text-slate-600">{c}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                </main>
                <PageFooter pageNumber={++pageNum + 1} totalPages={totalPages} clinicName={clinicSettings?.clinicName} />
            </Page>

            {/* PAGE 3: ANALYSIS */}
            <Page>
                <PageHeader subtitle="Current Analysis" accentColor={primaryColor} clinicName={clinicSettings?.clinicName} clinicLogo={clinicSettings?.clinicLogoBase64 ? `data:${clinicSettings.clinicLogoMimeType};base64,${clinicSettings.clinicLogoBase64}` : undefined} />
                <main className="flex-1 px-[12mm] py-4 grid grid-cols-12 gap-10 items-center">
                    <div className="col-span-5 relative">
                        <div className="aspect-square rounded-[32px] overflow-hidden shadow-2xl relative">
                            <img src={originalImageUrl} className="w-full h-full" style={{ objectFit: 'cover' }} alt="Analysis" />
                            <div className="absolute inset-0 bg-gradient-to-t from-slate-900/80 to-transparent opacity-80" />
                            <div className="absolute bottom-8 left-8 text-white">
                                <p className="text-[10px] font-bold tracking-[0.2em] opacity-70 mb-2 uppercase">Baseline Score</p>
                                <div className="flex items-baseline gap-2">
                                    <span className="text-7xl leading-none" style={{ fontFamily: "'DM Serif Display', serif" }}>{scoreBefore}</span>
                                    <span className="text-xl opacity-60">/100</span>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="col-span-7">
                        <SectionTitle overline="Diagnostics" title="Understanding The Canvas" accentColor={primaryColor} subtitle="A detailed assessment of your current dental aesthetics identifying key areas for enhancement." />
                        <div className="space-y-4 mt-8">
                            {(reportData.beforeAnalysis?.keyAreas || []).map((area: any, idx: number) => (
                                <div key={idx} className="p-4 rounded-xl bg-white border border-slate-100 shadow-sm flex gap-4">
                                    <div className="w-1 h-auto rounded-full bg-slate-200" style={{ backgroundColor: area.opportunityLevel.includes('High') ? '#F43F5E' : area.opportunityLevel.includes('Mod') ? '#F59E0B' : '#10B981' }} />
                                    <div>
                                        <div className="flex justify-between mb-1">
                                            <h4 className="font-bold text-xs uppercase tracking-wider text-slate-800">{area.area}</h4>
                                            <span className="text-[9px] font-bold px-2 py-0.5 rounded-full bg-slate-50 text-slate-400">{area.opportunityLevel}</span>
                                        </div>
                                        <p className="text-xs text-slate-500 leading-relaxed">{area.evaluation}</p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </main>
                <PageFooter pageNumber={++pageNum + 1} totalPages={totalPages} clinicName={clinicSettings?.clinicName} />
            </Page>

            {/* PAGE 4: THE VISION */}
            <Page>
                <PageHeader subtitle="Projected Result" accentColor={primaryColor} clinicName={clinicSettings?.clinicName} clinicLogo={clinicSettings?.clinicLogoBase64 ? `data:${clinicSettings.clinicLogoMimeType};base64,${clinicSettings.clinicLogoBase64}` : undefined} />
                <main className="flex-1 px-[12mm] py-4 flex gap-8">
                    <div className="flex-1 bg-white rounded-3xl border border-slate-200 p-2 shadow-2xl relative overflow-hidden group">
                        <img src={generatedImageUrl} className="w-full h-full rounded-2xl" style={{ objectFit: 'cover' }} alt="Vision" />
                        <div className="absolute bottom-0 inset-x-0 h-1/2 bg-gradient-to-t from-black/80 to-transparent" />
                        <div className="absolute bottom-8 left-8 right-8 text-white">
                            <div className="flex items-center gap-2 mb-4">
                                <div className="bg-white/20 backdrop-blur-md border border-white/20 px-3 py-1 rounded-full text-[10px] font-bold tracking-widest uppercase flex items-center gap-2">
                                    <Sparkles className="w-3 h-3 text-yellow-300" /> AI Simulation
                                </div>
                            </div>
                            <p className="text-2xl font-light leading-snug opacity-90">{reportData.afterVision?.summary || 'A balanced, harmonious smile design.'}</p>
                        </div>
                    </div>
                    <div className="w-[300px] flex flex-col justify-center">
                        <SectionTitle overline="The Goal" title="Visualizing Perfection" accentColor={primaryColor} />
                        <div className="mt-8 space-y-6">
                            <ScoreRing score={scoreAfter} label="Projected Score" size={140} color={primaryColor} />
                            <div className="p-4 bg-slate-50 rounded-xl border border-slate-100 text-center">
                                <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-2">Improvement</p>
                                <p className="text-3xl font-regular text-slate-800" style={{ fontFamily: "'DM Serif Display', serif", color: primaryColor }}>+{improvement}%</p>
                            </div>
                        </div>
                    </div>
                </main>
                <PageFooter pageNumber={++pageNum + 1} totalPages={totalPages} clinicName={clinicSettings?.clinicName} />
            </Page>

            {/* PAGE 5: METRICS (RADAR) */}
            <Page>
                <PageHeader subtitle="Aesthetic Metrics" accentColor={primaryColor} clinicName={clinicSettings?.clinicName} clinicLogo={clinicSettings?.clinicLogoBase64 ? `data:${clinicSettings.clinicLogoMimeType};base64,${clinicSettings.clinicLogoBase64}` : undefined} />
                <main className="flex-1 px-[12mm] py-4 flex items-center justify-center gap-12">
                    <div className="w-1/2 flex justify-center">
                        <div className="relative">
                            <div className="absolute inset-0 bg-slate-100/50 rounded-full blur-3xl" />
                            <EliteRadarChart before={radarBefore} after={radarAfter} color={primaryColor} />
                        </div>
                    </div>
                    <div className="w-1/2">
                        <SectionTitle overline="Data Analysis" title="Quantifiable Beauty" accentColor={primaryColor} />
                        <div className="mt-8 space-y-2">
                            {(reportData.transformationAnalysis?.comparativeMetrics || []).map((m: any, i: number) => (
                                <ComparisonMetricBar key={i} label={m.area} before={m.beforeScore} after={m.afterScore} color={primaryColor} />
                            ))}
                        </div>
                    </div>
                </main>
                <PageFooter pageNumber={++pageNum + 1} totalPages={totalPages} clinicName={clinicSettings?.clinicName} />
            </Page>

            {/* PAGE 6: COMPARISON */}
            <Page>
                <PageHeader subtitle="Transformation" accentColor={primaryColor} clinicName={clinicSettings?.clinicName} clinicLogo={clinicSettings?.clinicLogoBase64 ? `data:${clinicSettings.clinicLogoMimeType};base64,${clinicSettings.clinicLogoBase64}` : undefined} />
                <main className="flex-1 px-[12mm] py-4 flex flex-col">
                    <div className="text-center mb-6">
                        <SectionTitle overline="Side by Side" title="The Difference" accentColor={primaryColor} centered />
                    </div>
                    <div className="flex-1 grid grid-cols-2 gap-4">
                        <div className="relative rounded-2xl overflow-hidden border-4 border-white shadow-lg group">
                            <img src={originalImageUrl} className="w-full h-full filter grayscale-[10%]" style={{ objectFit: 'cover' }} alt="Before" />
                            <div className="absolute top-4 left-4 bg-white/90 backdrop-blur px-4 py-1.5 rounded-full text-[10px] font-bold tracking-widest text-slate-800">BEFORE</div>
                        </div>
                        <div className="relative rounded-2xl overflow-hidden border-4 border-white shadow-xl ring-1 ring-slate-100 group">
                            <img src={generatedImageUrl} className="w-full h-full" style={{ objectFit: 'cover' }} alt="After" />
                            <div className="absolute top-4 right-4 bg-slate-900/90 backdrop-blur px-4 py-1.5 rounded-full text-[10px] font-bold tracking-widest text-white" style={{ backgroundColor: primaryColor }}>AFTER</div>
                        </div>
                    </div>
                    <div className="mt-6 flex justify-center">
                        <div className="flex items-center gap-12 px-8 py-4 rounded-2xl bg-white border border-slate-100 shadow-sm">
                            <div className="text-center">
                                <p className="text-[10px] uppercase font-bold text-slate-400 tracking-wider">Starting</p>
                                <p className="text-2xl text-slate-300 font-bold">{scoreBefore}</p>
                            </div>
                            <ArrowRightIcon className="text-slate-200" />
                            <div className="text-center">
                                <p className="text-[10px] uppercase font-bold tracking-wider" style={{ color: primaryColor }}>Result</p>
                                <p className="text-3xl font-bold" style={{ color: primaryColor }}>{scoreAfter}</p>
                            </div>
                        </div>
                    </div>
                </main>
                <PageFooter pageNumber={++pageNum + 1} totalPages={totalPages} clinicName={clinicSettings?.clinicName} />
            </Page>

            {/* PAGE 7: JOURNEY */}
            <Page>
                <PageHeader subtitle="Timeline" accentColor={primaryColor} clinicName={clinicSettings?.clinicName} clinicLogo={clinicSettings?.clinicLogoBase64 ? `data:${clinicSettings.clinicLogoMimeType};base64,${clinicSettings.clinicLogoBase64}` : undefined} />
                <main className="flex-1 px-[12mm] py-4 flex flex-col justify-center">
                    <SectionTitle overline="Roadmap" title="Your Treatment Journey" accentColor={primaryColor} centered subtitle="A clear, structured pathway to achieving your ideal smile." />
                    <div className="mt-12 w-full max-w-4xl mx-auto relative">
                        <div className="absolute top-1/2 left-0 w-full h-0.5 bg-slate-100 -translate-y-1/2 z-0" />
                        <div className="grid grid-cols-4 gap-4 relative z-10">
                            {['Consultation', 'Design', 'Procedure', 'Reveal'].map((step, i) => (
                                <div key={i} className="flex flex-col items-center gap-6 group">
                                    <div className={`w-16 h-16 rounded-2xl flex items-center justify-center text-xl font-bold bg-white border-4 shadow-sm transition-all duration-500 group-hover:-translate-y-2 ${i === 3 ? 'shadow-lg border-2' : 'border-white'}`} style={{ borderColor: i === 3 ? primaryColor : 'white', color: i === 3 ? primaryColor : '#94A3B8' }}>
                                        {i + 1}
                                    </div>
                                    <div className="text-center">
                                        <p className="text-xs font-bold uppercase tracking-widest text-slate-800 mb-1">{step}</p>
                                        <p className="text-[10px] text-slate-400 max-w-[120px] mx-auto leading-relaxed">Step {i + 1} description goes here.</p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </main>
                <PageFooter pageNumber={++pageNum + 1} totalPages={totalPages} clinicName={clinicSettings?.clinicName} />
            </Page>

            {/* PAGE 8: PORTRAIT */}
            {hasPortrait && (
                <Page>
                    <PageHeader subtitle="Portrait Mode" accentColor={primaryColor} clinicName={clinicSettings?.clinicName} clinicLogo={clinicSettings?.clinicLogoBase64 ? `data:${clinicSettings.clinicLogoMimeType};base64,${clinicSettings.clinicLogoBase64}` : undefined} />
                    <main className="flex-1 px-[12mm] py-4 flex items-center justify-center relative">
                        <div className="absolute inset-0 flex items-center justify-center opacity-[0.03]">
                            <span className="text-[200px] font-bold" style={{ fontFamily: "'DM Serif Display', serif" }}>Smile</span>
                        </div>
                        <div className="relative w-[320px] aspect-[3/4] p-3 bg-white shadow-2xl skew-y-1 rounded-sm">
                            <div className="w-full h-full border-[10px] border-slate-50 overflow-hidden relative">
                                <img src={patientPortraitUrl} className="w-full h-full" style={{ objectFit: 'cover' }} alt="Portrait" />
                                <div className="absolute bottom-6 left-0 right-0 text-center">
                                    <p className="font-signature text-3xl text-white drop-shadow-md">Confidence Unlocked</p>
                                </div>
                            </div>
                        </div>
                        <div className="absolute bottom-12 right-12 max-w-xs text-right">
                            <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-2">Patient Portrait</p>
                            <p className="text-xl text-slate-600 font-light italic leading-relaxed">"The most beautiful curve on a woman's body is her smile."</p>
                        </div>
                    </main>
                    <PageFooter pageNumber={++pageNum + 1} totalPages={totalPages} clinicName={clinicSettings?.clinicName} />
                </Page>
            )}

            {/* PAGE 9/10: CONCLUSION */}
            <Page>
                <PageHeader subtitle="Next Steps" accentColor={primaryColor} clinicName={clinicSettings?.clinicName} clinicLogo={clinicSettings?.clinicLogoBase64 ? `data:${clinicSettings.clinicLogoMimeType};base64,${clinicSettings.clinicLogoBase64}` : undefined} />
                <main className="flex-1 px-[12mm] py-4 flex flex-col items-center justify-center">
                    <div className="w-full max-w-3xl bg-white rounded-[32px] shadow-2xl border border-slate-100 overflow-hidden flex flex-col md:flex-row">
                        <div className="w-2/5 bg-slate-900 p-10 text-white flex flex-col justify-between relative overflow-hidden">
                            <div className="absolute inset-0 opacity-20 bg-[radial-gradient(circle_at_top_right,_var(--tw-gradient-stops))] from-white/20 to-transparent" />
                            <div>
                                <div className="w-12 h-12 rounded-xl bg-white/10 flex items-center justify-center mb-6"><CalendarIcon className="w-6 h-6" /></div>
                                <h3 className="text-2xl font-light mb-2">Ready to proceed?</h3>
                                <p className="text-sm opacity-60">Schedule your clinical consultation today.</p>
                            </div>
                            <div className="space-y-4">
                                <div className="text-xs font-bold uppercase tracking-widest opacity-40">Contact Us</div>
                                <div className="text-sm opacity-80 space-y-1">
                                    <p>{clinicSettings?.clinicPhone || '(555) 123-4567'}</p>
                                    <p>{clinicSettings?.clinicEmail || 'clinic@example.com'}</p>
                                </div>
                            </div>
                        </div>
                        <div className="w-3/5 p-10 flex flex-col justify-center items-center">
                            <div className="w-32 h-32 bg-slate-50 rounded-2xl mb-6 flex items-center justify-center border-2 border-dashed border-slate-200">
                                {clinicSettings?.clinicWebsite ? (
                                    <img src={`https://api.qrserver.com/v1/create-qr-code/?size=150x150&data=${encodeURIComponent(clinicSettings.clinicWebsite)}&color=${primaryColor.replace('#', '')}`} className="w-24 h-24 mix-blend-multiply" />
                                ) : (
                                    <span className="text-xs text-slate-300 font-bold uppercase">QR Code</span>
                                )}
                            </div>
                            <p className="text-xs text-slate-500 font-medium uppercase tracking-wide">Scan to Book Appointment</p>
                        </div>
                    </div>
                    <p className="mt-12 text-[10px] text-slate-400 max-w-xl text-center leading-relaxed">
                        {reportData.nextSteps?.disclaimer || 'This is an informative simulation. Final results depend on clinical feasibility.'}
                        <br />Generated by FuturaSmile AI.
                    </p>
                </main>
                <PageFooter pageNumber={++pageNum + 1} totalPages={totalPages} clinicName={clinicSettings?.clinicName} />
            </Page>
        </div>
    );
};

export default CaseReportPDFElite;
